<template>
  <div class="careers">
    <!-- 页面标题 -->
    <section class="page-header bg-success text-white py-5">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-8">
            <h1 class="page-title">工作机会</h1>
            <p class="page-subtitle">加入我们，共创未来</p>
          </div>
          <div class="col-md-4 text-md-right">
            <nav aria-label="breadcrumb">
              <ol class="breadcrumb bg-transparent text-white mb-0">
                <li class="breadcrumb-item"><a href="/" class="text-white">首页</a></li>
                <li class="breadcrumb-item active text-white-50" aria-current="page">工作机会</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
    </section>

    <!-- 招聘介绍 -->
    <section class="careers-intro py-5">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <h2 class="section-title mb-4">为什么选择我们？</h2>
            <p class="lead">
              我们是一家充满活力的IT技术服务公司，提供广阔的发展平台和具有竞争力的薪酬福利。
              在这里，你将与一群优秀的技术专家共事，参与具有挑战性的项目，实现个人价值。
            </p>
            <div class="benefits-list">
              <div class="benefit-item d-flex align-items-center mb-3">
                <i class="fas fa-check-circle text-success mr-3"></i>
                <span>具有竞争力的薪酬待遇</span>
              </div>
              <div class="benefit-item d-flex align-items-center mb-3">
                <i class="fas fa-check-circle text-success mr-3"></i>
                <span>完善的培训体系和职业发展路径</span>
              </div>
              <div class="benefit-item d-flex align-items-center mb-3">
                <i class="fas fa-check-circle text-success mr-3"></i>
                <span>弹性工作制和远程办公机会</span>
              </div>
              <div class="benefit-item d-flex align-items-center mb-3">
                <i class="fas fa-check-circle text-success mr-3"></i>
                <span>丰富的团队活动和员工关怀</span>
              </div>
            </div>
          </div>
          <div class="col-lg-6">
            <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600" 
                 alt="团队工作" class="img-fluid rounded shadow">
          </div>
        </div>
      </div>
    </section>

    <!-- 职位列表 -->
    <section class="job-listings py-5 bg-light">
      <div class="container">
        <div class="section-header text-center mb-5">
          <h2 class="section-title">开放职位</h2>
          <p class="section-subtitle">寻找志同道合的伙伴</p>
        </div>
        
        <!-- 职位筛选 -->
        <div class="job-filters mb-4">
          <div class="row">
            <div class="col-md-4 mb-3">
              <select class="form-control" v-model="selectedDepartment">
                <option value="">所有部门</option>
                <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
              </select>
            </div>
            <div class="col-md-4 mb-3">
              <select class="form-control" v-model="selectedLocation">
                <option value="">所有地点</option>
                <option v-for="location in locations" :key="location" :value="location">{{ location }}</option>
              </select>
            </div>
            <div class="col-md-4 mb-3">
              <select class="form-control" v-model="selectedType">
                <option value="">所有类型</option>
                <option v-for="type in jobTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- 职位卡片 -->
        <div class="job-cards">
          <div 
            v-for="job in filteredJobs" 
            :key="job.id"
            class="job-card card mb-4 border-0 shadow-sm"
          >
            <div class="card-body">
              <div class="row align-items-center">
                <div class="col-md-8">
                  <h5 class="job-title mb-2">{{ job.title }}</h5>
                  <div class="job-meta mb-2">
                    <span class="job-department badge badge-primary mr-2">{{ job.department }}</span>
                    <span class="job-location mr-3">
                      <i class="fas fa-map-marker-alt mr-1"></i>{{ job.location }}
                    </span>
                    <span class="job-type">
                      <i class="fas fa-clock mr-1"></i>{{ job.type }}
                    </span>
                  </div>
                  <p class="job-description mb-3">{{ job.description }}</p>
                  <div class="job-tags">
                    <span 
                      v-for="tag in job.tags" 
                      :key="tag"
                      class="badge badge-light mr-2"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </div>
                <div class="col-md-4 text-md-right">
                  <div class="job-actions">
                    <button 
                      class="btn btn-success mb-2"
                      @click="applyJob(job)"
                    >
                      立即申请
                    </button>
                    <button 
                      class="btn btn-outline-secondary btn-sm"
                      @click="viewJobDetails(job)"
                    >
                      查看详情
                    </button>
                  </div>
                  <div class="job-date text-muted mt-2">
                    <small>发布于 {{ formatDate(job.postedDate) }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 无结果提示 -->
        <div v-if="filteredJobs.length === 0" class="text-center py-5">
          <i class="fas fa-search fa-3x text-muted mb-3"></i>
          <h5 class="text-muted">暂无匹配的职位</h5>
          <p class="text-muted">请尝试调整筛选条件或关注后续更新</p>
        </div>
      </div>
    </section>

    <!-- 员工福利 -->
    <section class="employee-benefits py-5">
      <div class="container">
        <div class="section-header text-center mb-5">
          <h2 class="section-title">员工福利</h2>
          <p class="section-subtitle">我们关心每一位员工的成长与幸福</p>
        </div>
        <div class="row">
          <div class="col-lg-3 col-md-6 mb-4" v-for="benefit in benefits" :key="benefit.id">
            <div class="benefit-card text-center">
              <div class="benefit-icon mb-3">
                <i :class="benefit.icon" class="fa-3x text-success"></i>
              </div>
              <h5 class="benefit-title">{{ benefit.title }}</h5>
              <p class="benefit-description">{{ benefit.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 招聘流程 -->
    <section class="recruitment-process py-5 bg-light">
      <div class="container">
        <div class="section-header text-center mb-5">
          <h2 class="section-title">招聘流程</h2>
          <p class="section-subtitle">简单透明的招聘流程</p>
        </div>
        <div class="row">
          <div class="col-md-3 mb-4" v-for="step in recruitmentSteps" :key="step.id">
            <div class="process-step text-center">
              <div class="step-number mb-3">
                <span class="badge badge-success rounded-circle p-3 h4">{{ step.step }}</span>
              </div>
              <h5 class="step-title">{{ step.title }}</h5>
              <p class="step-description">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 职位详情模态框 -->
    <div class="modal fade" id="jobModal" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ currentJob.title }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>×</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="job-details">
              <div class="job-info mb-4">
                <div class="row">
                  <div class="col-md-6">
                    <p><strong>部门：</strong>{{ currentJob.department }}</p>
                    <p><strong>地点：</strong>{{ currentJob.location }}</p>
                  </div>
                  <div class="col-md-6">
                    <p><strong>类型：</strong>{{ currentJob.type }}</p>
                    <p><strong>薪资：</strong>{{ currentJob.salary }}</p>
                  </div>
                </div>
              </div>

              <div class="job-requirements mb-4">
                <h6>职位要求：</h6>
                <ul>
                  <li v-for="requirement in currentJob.requirements" :key="requirement">
                    {{ requirement }}
                  </li>
                </ul>
              </div>

              <div class="job-responsibilities mb-4">
                <h6>工作职责：</h6>
                <ul>
                  <li v-for="responsibility in currentJob.responsibilities" :key="responsibility">
                    {{ responsibility }}
                  </li>
                </ul>
              </div>

              <div class="job-benefits">
                <h6>福利待遇：</h6>
                <ul>
                  <li v-for="benefit in currentJob.benefits" :key="benefit">
                    {{ benefit }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-success" @click="submitApplication">立即申请</button>
            <button type="button" class="btn btn-secondary" data-dismiss="modal">关闭</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 申请表单模态框 -->
    <div class="modal fade" id="applicationModal" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">职位申请 - {{ currentJob.title }}</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>×</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitApplicationForm">
              <div class="form-group">
                <label>姓名 *</label>
                <input type="text" class="form-control" v-model="applicationForm.name" required>
              </div>
              <div class="form-group">
                <label>邮箱 *</label>
                <input type="email" class="form-control" v-model="applicationForm.email" required>
              </div>
              <div class="form-group">
                <label>电话 *</label>
                <input type="tel" class="form-control" v-model="applicationForm.phone" required>
              </div>
              <div class="form-group">
                <label>工作经验</label>
                <select class="form-control" v-model="applicationForm.experience">
                  <option value="">请选择</option>
                  <option value="0-1">0-1年</option>
                  <option value="1-3">1-3年</option>
                  <option value="3-5">3-5年</option>
                  <option value="5-10">5-10年</option>
                  <option value="10+">10年以上</option>
                </select>
              </div>
              <div class="form-group">
                <label>简历链接</label>
                <input type="url" class="form-control" v-model="applicationForm.resume" placeholder="在线简历链接">
              </div>
              <div class="form-group">
                <label>自我介绍</label>
                <textarea class="form-control" v-model="applicationForm.introduction" rows="4" 
                          placeholder="请简单介绍一下自己..."></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-success" @click="submitApplicationForm">提交申请</button>
            <button type="button" class="btn btn-secondary" data-dismiss="modal">取消</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Careers',
  data() {
    return {
      selectedDepartment: '',
      selectedLocation: '',
      selectedType: '',
      currentJob: {},
      applicationForm: {
        name: '',
        email: '',
        phone: '',
        experience: '',
        resume: '',
        introduction: ''
      },
      departments: ['技术部', '产品部', '设计部', '市场部', '销售部', '运营部'],
      locations: ['北京', '上海', '深圳', '广州', '杭州', '成都'],
      jobTypes: ['全职', '兼职', '实习', '远程'],
      jobs: [
        {
          id: 1,
          title: '高级前端开发工程师',
          department: '技术部',
          location: '北京',
          type: '全职',
          description: '负责公司核心产品的前端架构设计和开发，带领团队完成技术攻关',
          requirements: [
            '5年以上前端开发经验，精通Vue.js/React',
            '熟悉前端工程化和性能优化',
            '具备良好的团队协作和沟通能力',
            '有大型项目架构设计经验优先'
          ],
          responsibilities: [
            '负责前端技术架构设计',
            '带领团队完成核心功能开发',
            '制定前端开发规范',
            '解决技术难题'
          ],
          benefits: [
            '具有竞争力的薪酬',
            '股票期权',
            '技术培训',
            '弹性工作'
          ],
          salary: '25k-35k',
          tags: ['Vue.js', 'React', '前端架构', '团队管理'],
          postedDate: '2024-01-15'
        },
        {
          id: 2,
          title: '产品经理',
          department: '产品部',
          location: '上海',
          type: '全职',
          description: '负责产品规划、需求分析和用户体验设计，推动产品持续优化',
          requirements: [
            '3年以上产品经理经验',
            '熟悉产品设计流程和方法论',
            '具备良好的用户研究和数据分析能力',
            '有B端产品经验优先'
          ],
          responsibilities: [
            '产品规划和需求分析',
            '用户体验设计',
            '产品迭代优化',
            '跨部门协调'
          ],
          benefits: [
            '具有竞争力的薪酬',
            '产品奖金',
            '培训机会',
            '晋升空间'
          ],
          salary: '20k-30k',
          tags: ['产品设计', '用户体验', '数据分析', '项目管理'],
          postedDate: '2024-01-12'
        },
        {
          id: 3,
          title: 'UI/UX设计师',
          department: '设计部',
          location: '深圳',
          type: '全职',
          description: '负责产品界面设计和用户体验优化，创造优秀的视觉体验',
          requirements: [
            '3年以上UI/UX设计经验',
            '精通Figma、Sketch等设计工具',
            '具备良好的审美和创新能力',
            '有移动端设计经验优先'
          ],
          responsibilities: [
            '界面设计',
            '用户体验优化',
            '设计规范制定',
            '设计评审'
          ],
          benefits: [
            '具有竞争力的薪酬',
            '设计培训',
            '创意空间',
            '团队氛围'
          ],
          salary: '15k-25k',
          tags: ['UI设计', 'UX设计', 'Figma', '移动端'],
          postedDate: '2024-01-10'
        },
        {
          id: 4,
          title: 'Java后端开发工程师',
          department: '技术部',
          location: '杭州',
          type: '全职',
          description: '负责后端服务开发和系统架构设计，保障系统稳定性和性能',
          requirements: [
            '3年以上Java开发经验',
            '精通Spring Boot、MyBatis等框架',
            '熟悉微服务架构',
            '有分布式系统经验优先'
          ],
          responsibilities: [
            '后端服务开发',
            '系统架构设计',
            '性能优化',
            '技术攻关'
          ],
          benefits: [
            '具有竞争力的薪酬',
            '技术挑战',
            '成长机会',
            '团队支持'
          ],
          salary: '20k-35k',
          tags: ['Java', 'Spring Boot', '微服务', '分布式'],
          postedDate: '2024-01-08'
        },
        {
          id: 5,
          title: '前端开发实习生',
          department: '技术部',
          location: '北京',
          type: '实习',
          description: '参与前端开发工作，学习前沿技术，积累项目经验',
          requirements: [
            '计算机相关专业在校生',
            '熟悉HTML、CSS、JavaScript',
            '对前端技术有浓厚兴趣',
            '学习能力强'
          ],
          responsibilities: [
            '参与前端开发',
            '学习新技术',
            '协助项目开发',
            '技术文档编写'
          ],
          benefits: [
            '实习津贴',
            '技术培训',
            '导师指导',
            '转正机会'
          ],
          salary: '3k-5k',
          tags: ['前端开发', '实习生', '学习机会', '转正机会'],
          postedDate: '2024-01-05'
        }
      ],
      benefits: [
        {
          id: 1,
          title: '薪酬福利',
          icon: 'fas fa-money-bill-wave',
          description: '具有竞争力的薪酬待遇，年终奖，股票期权'
        },
        {
          id: 2,
          title: '健康保障',
          icon: 'fas fa-heartbeat',
          description: '五险一金，商业保险，年度体检，健身房'
        },
        {
          id: 3,
          title: '培训发展',
          icon: 'fas fa-graduation-cap',
          description: '技术培训，外部培训，职业发展规划'
        },
        {
          id: 4,
          title: '工作环境',
          icon: 'fas fa-coffee',
          description: '弹性工作制，远程办公，舒适办公环境'
        },
        {
          id: 5,
          title: '团队活动',
          icon: 'fas fa-users',
          description: '团建活动，年度旅游，节日福利'
        },
        {
          id: 6,
          title: '晋升机会',
          icon: 'fas fa-chart-line',
          description: '明确的晋升通道，内部轮岗机会'
        }
      ],
      recruitmentSteps: [
        {
          id: 1,
          step: '1',
          title: '在线申请',
          description: '提交简历和申请信息'
        },
        {
          id: 2,
          step: '2',
          title: '简历筛选',
          description: 'HR初步筛选简历'
        },
        {
          id: 3,
          step: '3',
          title: '技术面试',
          description: '技术能力评估'
        },
        {
          id: 4,
          step: '4',
          title: '综合面试',
          description: '综合素质评估'
        },
        {
          id: 5,
          step: '5',
          title: '发放offer',
          description: '确认录用意向'
        }
      ]
    }
  },
  computed: {
    filteredJobs() {
      return this.jobs.filter(job => {
        return (!this.selectedDepartment || job.department === this.selectedDepartment) &&
               (!this.selectedLocation || job.location === this.selectedLocation) &&
               (!this.selectedType || job.type === this.selectedType)
      })
    }
  },
  mounted() {
    this.initWOW()
  },
  methods: {
    initWOW() {
      if (typeof WOW !== 'undefined') {
        new WOW().init()
      }
    },
    viewJobDetails(job) {
      this.currentJob = job
      $('#jobModal').modal('show')
    },
    applyJob(job) {
      this.currentJob = job
      $('#applicationModal').modal('show')
    },
    submitApplication() {
      $('#jobModal').modal('hide')
      $('#applicationModal').modal('show')
    },
    submitApplicationForm() {
      // 这里可以添加表单提交逻辑
      alert('申请已提交，我们会尽快联系您！')
      $('#applicationModal').modal('hide')
      this.resetApplicationForm()
    },
    resetApplicationForm() {
      this.applicationForm = {
        name: '',
        email: '',
        phone: '',
        experience: '',
        resume: '',
        introduction: ''
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
}

.benefit-card {
  padding: 2rem;
  transition: transform 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-5px);
}

.benefit-icon {
  color: #28a745;
}

.job-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.job-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1) !important;
}

.job-title {
  color: #28a745;
}

.job-meta {
  font-size: 0.9rem;
}

.job-tags .badge {
  font-size: 0.8rem;
}

.process-step {
  padding: 1rem;
}

.step-number {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.step-title {
  color: #28a745;
  margin-bottom: 0.5rem;
}

.step-description {
  color: #6c757d;
  font-size: 0.9rem;
}

.benefits-list {
  margin-top: 2rem;
}

.benefit-item {
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .job-card .row {
    flex-direction: column;
  }
  
  .job-actions {
    text-align: center;
    margin-top: 1rem;
  }
}
</style>